import { useState, useCallback, useRef } from 'react'
// @ts-ignore - no type declarations available for @rails/activestorage
import { DirectUpload } from '@rails/activestorage'

export interface FileUploadProgress {
  file: File
  status: 'pending' | 'uploading' | 'completed' | 'error'
  progress: number // 0-100
  uploadSpeed: number // bytes per second
  timeRemaining: number // seconds
  signedBlobId?: string
  url?: string // Original blob URL
  smallUrl?: string // Small variant URL (~40KB) - only for images
  error?: string
}

export interface UploadedFile {
  signedBlobId: string
  url: string // Original blob URL
  smallUrl?: string // Small variant URL (240×180, ~40KB) - only for images
  blobUrl: string // Local blob URL for instant preview
  filename: string
  contentType: string
  byteSize: number
}

interface UseDirectUploadReturn {
  uploadProgress: Map<string, FileUploadProgress>
  uploadFiles: (files: File[]) => Promise<UploadedFile[]>
  cancelUpload: (fileKey: string) => void
  retryUpload: (fileKey: string) => Promise<void>
  isUploading: boolean
}

const DIRECT_UPLOAD_URL = '/rails/active_storage/direct_uploads'

// Helper to detect if we should use direct upload
const shouldUseDirectUpload = (): boolean => {
  // Always use direct upload (S3 configured in all environments)
  return true
}

// Helper to generate unique file key
const getFileKey = (file: File): string => {
  return `${file.name}-${file.size}-${file.lastModified}`
}

export function useDirectUpload(): UseDirectUploadReturn {
  const [uploadProgress, setUploadProgress] = useState<Map<string, FileUploadProgress>>(new Map())
  const [isUploading, setIsUploading] = useState(false)
  const abortControllers = useRef<Map<string, AbortController>>(new Map())
  const uploadStartTimes = useRef<Map<string, number>>(new Map())
  const lastProgressUpdate = useRef<Map<string, { loaded: number; timestamp: number }>>(new Map())

  const updateProgress = useCallback((fileKey: string, updates: Partial<FileUploadProgress>) => {
    setUploadProgress(prev => {
      const newMap = new Map(prev)
      const current = newMap.get(fileKey)
      if (current) {
        newMap.set(fileKey, { ...current, ...updates })
      }
      return newMap
    })
  }, [])

  const calculateUploadMetrics = useCallback((fileKey: string, loaded: number, total: number) => {
    const now = Date.now()
    const lastUpdate = lastProgressUpdate.current.get(fileKey)

    const progress = Math.round((loaded / total) * 100)

    let uploadSpeed = 0
    let timeRemaining = 0

    if (lastUpdate) {
      const timeDelta = (now - lastUpdate.timestamp) / 1000 // seconds
      const bytesDelta = loaded - lastUpdate.loaded

      if (timeDelta > 0) {
        uploadSpeed = bytesDelta / timeDelta

        if (uploadSpeed > 0) {
          timeRemaining = (total - loaded) / uploadSpeed
        }
      }
    }

    lastProgressUpdate.current.set(fileKey, { loaded, timestamp: now })

    return { progress, uploadSpeed, timeRemaining }
  }, [])

  const uploadFileToS3 = useCallback(async (file: File): Promise<UploadedFile> => {
    const fileKey = getFileKey(file)

    // Create blob URL immediately for instant preview
    const blobUrl = URL.createObjectURL(file)

    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, DIRECT_UPLOAD_URL, {
        directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
          // Setup abort controller
          const controller = new AbortController()
          abortControllers.current.set(fileKey, controller)

          // Handle abort
          controller.signal.addEventListener('abort', () => {
            xhr.abort()
          })

          // Track upload progress
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const metrics = calculateUploadMetrics(fileKey, event.loaded, event.total)

              updateProgress(fileKey, {
                status: 'uploading',
                ...metrics
              })
            }
          })
        }
      })

      // Record start time
      uploadStartTimes.current.set(fileKey, Date.now())
      lastProgressUpdate.current.set(fileKey, { loaded: 0, timestamp: Date.now() })

      // Start upload
      upload.create((error: Error | null, blob: any) => {
        if (error) {
          // Clean up blob URL on error
          URL.revokeObjectURL(blobUrl)
          updateProgress(fileKey, {
            status: 'error',
            error: error.message || 'Upload failed'
          })
          abortControllers.current.delete(fileKey)
          uploadStartTimes.current.delete(fileKey)
          lastProgressUpdate.current.delete(fileKey)
          reject(error)
        } else {
          // S3 upload complete! Queue variant generation on server (async)
          // Don't wait for it - resolve immediately for faster UX
          fetch('/api/blob_variants', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
            },
            body: JSON.stringify({ signed_id: blob.signed_id })
          })
            .then(response => response.json())
            .then(data => {
              const originalUrl = data.original_url
              const smallUrl = data.small_url // Variant URL (processed async in background)

              // Complete immediately - don't wait for image preload
              updateProgress(fileKey, {
                status: 'completed',
                progress: 100,
                signedBlobId: blob.signed_id,
                url: originalUrl,
                smallUrl: smallUrl
              })
              abortControllers.current.delete(fileKey)
              uploadStartTimes.current.delete(fileKey)
              lastProgressUpdate.current.delete(fileKey)
              resolve({
                signedBlobId: blob.signed_id,
                url: originalUrl,
                smallUrl: smallUrl,
                blobUrl: blobUrl,
                filename: file.name,
                contentType: file.type,
                byteSize: file.size
              })

              // Preload variant in background (non-blocking)
              if (smallUrl && file.type.startsWith('image/')) {
                const preloadImage = new Image()
                preloadImage.src = smallUrl
              }
            })
            .catch(error => {
              // Variant endpoint failed, use local blob URL as fallback
              console.error('Failed to get variant URLs:', error)

              updateProgress(fileKey, {
                status: 'completed',
                progress: 100,
                signedBlobId: blob.signed_id,
                url: blobUrl // Use local blob URL - file is uploaded, just can't get server URL
              })
              abortControllers.current.delete(fileKey)
              uploadStartTimes.current.delete(fileKey)
              lastProgressUpdate.current.delete(fileKey)
              resolve({
                signedBlobId: blob.signed_id,
                url: blobUrl,
                blobUrl: blobUrl,
                filename: file.name,
                contentType: file.type,
                byteSize: file.size
              })
            })
        }
      })
    })
  }, [calculateUploadMetrics, updateProgress])

  const uploadFiles = useCallback(async (files: File[]): Promise<UploadedFile[]> => {
    if (files.length === 0) return []

    setIsUploading(true)

    try {
      // If not in production/staging, return empty array (files will be uploaded the old way)
      if (!shouldUseDirectUpload()) {
        return []
      }

      // Initialize progress for new files (merge with existing progress to keep completed uploads)
      setUploadProgress(prev => {
        const newMap = new Map(prev)
        files.forEach(file => {
          const fileKey = getFileKey(file)
          newMap.set(fileKey, {
            file,
            status: 'pending',
            progress: 0,
            uploadSpeed: 0,
            timeRemaining: 0
          })
        })
        return newMap
      })

      // Upload all files in parallel
      const uploadPromises = files.map(file => uploadFileToS3(file))
      const uploadedFiles = await Promise.all(uploadPromises)

      return uploadedFiles
    } catch (error) {
      console.error('❌ Upload failed:', error)
      throw error
    } finally {
      setIsUploading(false)
    }
  }, [uploadFileToS3])

  const cancelUpload = useCallback((fileKey: string) => {
    const controller = abortControllers.current.get(fileKey)
    if (controller) {
      controller.abort()
      updateProgress(fileKey, {
        status: 'error',
        error: 'Upload cancelled'
      })
    }
  }, [updateProgress])

  const retryUpload = useCallback(async (fileKey: string) => {
    const fileProgress = uploadProgress.get(fileKey)
    if (!fileProgress || fileProgress.status !== 'error') return

    try {
      updateProgress(fileKey, {
        status: 'pending',
        progress: 0,
        uploadSpeed: 0,
        timeRemaining: 0,
        error: undefined
      })

      const uploadedFile = await uploadFileToS3(fileProgress.file)

      updateProgress(fileKey, {
        signedBlobId: uploadedFile.signedBlobId,
        url: uploadedFile.url
      })
    } catch (error) {
      console.error('Retry upload failed:', error)
    }
  }, [uploadProgress, uploadFileToS3, updateProgress])

  return {
    uploadProgress,
    uploadFiles,
    cancelUpload,
    retryUpload,
    isUploading
  }
}
